import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { MainComponent } from '../main/main.component';
import { ModElemModel } from '../models/ecosat/mod-elem.model';
import { ElementoModel } from '../models/ecosat/elemento.model';
import { EmpresaSSOModel } from '../models/sso/empresa.model';
import { AplicacionModel } from '../models/sso/aplicacion.model';
import { AplicacionEmpresaModel } from '../models/sso/aplicacion-empresa.model';
import { TipoElemModel } from '../models/ecosat/tipo-elem.model';
import { BdtEquipamientoModel } from '../models/bdt/bdt-equipamiento.model';
import { ElementoNPModel } from '../models/np/elemento-np.model';
import { BdtCatalogoEquipModel } from '../models/bdt/bdt-cat-equip.model';
import { EmpresaIntraModel } from '../models/intranet/empresa.model';
import { TicketModel } from '../models/sso/ticket.model';
import { MovilModel } from '../models/np/movil-np.model';
import { PosicionModel } from '../models/ecosat/posicion.model';
import { PosicionNPModel } from '../models/np/posicion-np.model';
import { AppComponent } from '../app.component';
import { DateUtils } from '../utils/date-utils';

export interface IMap<T> {
  map: Map<string, Array<T>>;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private ticket: TicketModel;
  private timerEcoSAT = null;
  private timerNP = null;
  private timerSSO = null;
  private timerBDT = null;
  private timerIntranet = null;
  private ELEM_PAG = 1000;
  public static modElementos = new Map<number, ModElemModel>();
  public static equipamientos = new Map<number, BdtEquipamientoModel>();

  constructor(private http: HttpClient) {
  }

  // Obtiene un token de la API de EcoSAT
  async getEcoSATApiToken(): Promise<string> {
    let token = null;
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
      };
      const body = new URLSearchParams();
      body.set('grant_type', environment.grantTypeApiEcoSAT);
      body.set('client_id', environment.clientIdApiEcoSAT);
      body.set('client_secret', environment.clientSecretApiEcoSAT);
      body.set('username', environment.userNameApiEcoSAT);
      body.set('password', environment.passwordApiEcoSAT);
      const response = await this.http.post<string>(MainComponent.getInstance().urlApiEcoSAT + '/token',
        body.toString(), httpOptions).toPromise();
      if (response) {
        const data = JSON.parse(JSON.stringify(response, ['access_token', 'expires_in']));
        // Guardo el nuevo token y anpunto que ya se puede empezar la comunicación con la API
        sessionStorage.setItem('token-import-EcoSAT', data.access_token);
        // Activo un temporizador para pedir un nuevo token 2 minutos
        // antes que caduque el actual
        let interval = 60;
        if (data.expires_in > 120) {
          interval = data.expires_in - 120;
        }
        if (this.timerEcoSAT) {
          clearInterval(this.timerEcoSAT);
        }
        this.timerEcoSAT = setInterval(() => {
          this.getEcoSATApiToken();
        }, interval * 1000);
        token = data.access_token;
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return token;
  }

  // Obtiene un token de la API de la NP
  async getNPApiToken(): Promise<string> {
    let token = null;
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
      };
      const body = new URLSearchParams();
      body.set('grant_type', environment.grantTypeApi);
      body.set('client_id', environment.clientIdApiNP);
      body.set('client_secret', environment.clientSecretApiNP);
      body.set('username', environment.userNameApiNP);
      body.set('password', environment.passwordApiNP);
      const response = await this.http.post<string>(MainComponent.getInstance().urlApi + '/api/token',
        body.toString(), httpOptions).toPromise();
      if (response) {
        const data = JSON.parse(JSON.stringify(response, ['access_token', 'expires_in']));
        // Guardo el nuevo token y anpunto que ya se puede empezar la comunicación con la API
        sessionStorage.setItem('token-import-NP', data.access_token);
        // Activo un temporizador para pedir un nuevo token 2 minutos
        // antes que caduque el actual
        let interval = 60;
        if (data.expires_in > 120) {
          interval = data.expires_in - 120;
        }
        if (this.timerNP) {
          clearInterval(this.timerNP);
        }
        this.timerNP = setInterval(() => {
          this.getNPApiToken();
        }, interval * 1000);
        token = data.access_token;
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return token;
  }

  // Obtiene un token de la BDT
  async getBDTApiToken(): Promise<string> {
    let token = null;
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
      };
      const body = new URLSearchParams();
      body.set('grant_type', environment.grantTypeApiBDT);
      body.set('PASSWORD_CLIENT_ID', environment.clientIdApiBDT);
      body.set('PASSWORD_CLIENT_SECRET', environment.clientSecretApiBDT);
      body.set('email', environment.userNameApiBDT);
      body.set('password', environment.passwordApiBDT);
      const response = await this.http.post<string>(MainComponent.getInstance().urlApiBDT + '/login',
        body.toString(), httpOptions).toPromise();
      if (response) {
        const data = JSON.parse(JSON.stringify(response, ['access_token', 'expires_in']));
        // Guardo el nuevo token y anpunto que ya se puede empezar la comunicación con la API
        sessionStorage.setItem('token-import-BDT', data.access_token);
        // Activo un temporizador para pedir un nuevo token 2 minutos
        // antes que caduque el actual
        let interval = 60;
        if (data.expires_in > 120) {
          interval = data.expires_in - 120;
        }
        if (this.timerBDT) {
          clearInterval(this.timerBDT);
        }
        this.timerBDT = setInterval(() => {
          this.getBDTApiToken();
        }, interval * 1000);
        token = data.access_token;
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return token;
  }

  // Obtiene un token de la Intranet
  async getIntranetApiToken(): Promise<string> {
    let token = null;
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
      }; 7
      const body = new URLSearchParams();
      body.set('grant_type', environment.grantTypeApiIntranet);
      body.set('PASSWORD_CLIENT_ID', environment.clientIdApiIntranet);
      body.set('PASSWORD_CLIENT_SECRET', environment.clientSecretApiIntranet);
      body.set('email', environment.userNameApiIntranet);
      body.set('password', environment.passwordApiIntranet);
      const response = await this.http.post<string>(MainComponent.getInstance().urlApiIntranet + '/login',
        body.toString(), httpOptions).toPromise();
      if (response) {
        const data = JSON.parse(JSON.stringify(response, ['access_token', 'expires_in']));
        // Guardo el nuevo token y anpunto que ya se puede empezar la comunicación con la API
        sessionStorage.setItem('token-import-Intranet', data.access_token);
        // Activo un temporizador para pedir un nuevo token 2 minutos
        // antes que caduque el actual
        let interval = 60;
        if (data.expires_in > 120) {
          interval = data.expires_in - 120;
        }
        if (this.timerIntranet) {
          clearInterval(this.timerIntranet);
        }
        this.timerIntranet = setInterval(() => {
          this.getIntranetApiToken();
        }, interval * 1000);
        token = data.access_token;
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return token;
  }

  // Obtiene un token del SSO
  async getSSOApiToken(): Promise<string> {
    let token = null;
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
      };
      const body = new URLSearchParams();
      body.set('grant_type', environment.grantTypeApiSSO);
      body.set('client_id', environment.clientIdApiSSO);
      body.set('client_secret', environment.clientSecretApiSSO);
      body.set('username', environment.userNameApiSSO);
      body.set('password', environment.passwordApiSSO);
      const ppp = AppComponent.getInstance().urlApiSSO;
      const response = await this.http.post<string>(AppComponent.getInstance().urlApiSSO + '/sso/token',
        body.toString(), httpOptions).toPromise();
      if (response) {
        const data = JSON.parse(JSON.stringify(response, ['access_token', 'expires_in']));
        // Guardo el nuevo token y anpunto que ya se puede empezar la comunicación con la API
        sessionStorage.setItem('token-import-SSO', data.access_token);
        // Activo un temporizador para pedir un nuevo token 2 minutos
        // antes que caduque el actual
        let interval = 60;
        if (data.expires_in > 120) {
          interval = data.expires_in - 120;
        }
        if (this.timerSSO) {
          clearInterval(this.timerSSO);
        }
        this.timerSSO = setInterval(() => {
          this.getSSOApiToken();
        }, interval * 1000);
        token = data.access_token;
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return token;
  }

  // Devuelve el ticket almacenado actualmente
  public getTicket(): TicketModel {
    // Primero busco el que tengo en la sesión
    let ticket: TicketModel = <TicketModel>JSON.parse(sessionStorage.getItem('ticketSSO'));
    if (ticket === null) {
      // Si no hay en la sesión puede ser porque he iniciado la app escribiendo
      // directamente la URL en el navegador, entonces busco en el localstorage 
      // por si tengo un ticket válido almacenado de otro inicio
      ticket = <TicketModel>JSON.parse(localStorage.getItem('ticketSSO'));
      if (ticket !== null) {
        sessionStorage.setItem('ticketSSO', JSON.stringify(ticket));
      }
    }
    return ticket;
  }

  // Valida un ticket en el SSO y devuelve la información
  private async validaTicket(ticketId: string): Promise<TicketModel> {
    try {
      return await this.http.get<TicketModel>(AppComponent.getInstance().urlApiSSO + '/sso/ticket/valida/' + ticketId).toPromise();
    } catch (e) {
      console.log(e);
    }
    return null;
  }

  // Recupera la información de inicio de sesión del SSO
  public async initSession(ticketId: string): Promise<boolean> {
    try {
      this.ticket = null;
      if (ticketId !== undefined) {
        this.ticket = await this.validaTicket(ticketId);
        if (this.ticket !== null) {
          localStorage.setItem('ticketSSO', JSON.stringify(this.ticket));
          sessionStorage.setItem('ticketSSO', JSON.stringify(this.ticket));
          return true;
        }
      }
    } catch (e) {
      console.log(e);
    }
    sessionStorage.removeItem('ticketSSO');
    return false;
  }

  // Permite saber si un usuario está validado en el SSO
  public async isUsuarioValidado(userId: number): Promise<boolean> {
    try {
      return await this.http.get<boolean>(AppComponent.getInstance().urlApiSSO + '/sso/usuario/validado/' + userId).toPromise();
    } catch (e) {
      console.log(e);
    }
    return false;
  }

  // Borra el ticket almacenado
  public endAppSession(): void {
    sessionStorage.removeItem('ticketSSO');
    localStorage.removeItem('ticketSSO');
    window.location.replace(AppComponent.getInstance().webSSO);
  }

  // Recupera los modelos de elementos de la empresa en EcoSAT
  async getModElementosEcoSAT(empresaId: number) {
    let result: ModElemModel[] = [];
    try {
      ApiService.modElementos.clear();
      const tipos: TipoElemModel[] = await this.http.get<TipoElemModel[]>(MainComponent.getInstance().urlApiEcoSAT +
        '/api/elementos/tipos/' + empresaId).toPromise();
      if (tipos) {
        const tiposElem = new Map<number, TipoElemModel>();
        tipos.forEach(tipo => {
          tiposElem.set(tipo.Codigo, tipo);
        });
        const res = await this.http.get<ModElemModel[]>(MainComponent.getInstance().urlApiEcoSAT +
          '/api/elementos/modelos/' + empresaId).toPromise();
        if (res) {
          res.forEach((model, i) => {
            if (!model.FechaBaja) {
              model.visible = 'true';
              model.Unidades = 0;
              model.TipoModelo = tiposElem.get(model.CodTipo);
              if (!model.TipoModelo) {
                model.TipoModelo = new TipoElemModel(0, model.Abreviatura);
              }
              ApiService.modElementos.set(model.Codigo, model);
              result.push(model);
            }
          });
        }
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Recupera los elementos de la empresa
  async getElementosEcoSAT(empresaId: number): Promise<void> {
    try {
      let result: ElementoModel[] = [];
      if (!MainComponent.getInstance().cancelar) {
        const res = await this.http.get<ElementoModel[]>(MainComponent.getInstance().urlApiEcoSAT +
          '/api/elementos/lite/' + empresaId + '/1/' + this.ELEM_PAG).toPromise();
        if (res && res.length > 0) {
          const resultLength = res.length;
          const totalReg = res[0].TotalRegistros;
          res.forEach((elem, i) => {
            if (!elem.FechaBaja) {
              if (elem.FechaUltimaLecturaVolumetrico) {
                elem.FechaUltimaLecturaVolumetrico = new Date(elem.FechaUltimaLecturaVolumetrico);
              }
              if (!DateUtils.isValidDate(elem.FechaUltimaLecturaVolumetrico)) {
                elem.FechaUltimaLecturaVolumetrico = new Date(1970, 1, 1);
              }
              result.push(elem);
            }
          });
          if (result.length > 0) {
            MainComponent.getInstance().onNewElements(totalReg, result);
          }
          if (resultLength < totalReg) {
            await this.getElementosPagina(empresaId, 2, resultLength);
          }
        }
      } else {
        MainComponent.getInstance().logError('Proceso cancelado por el usuario');
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
  }

  // Recupera un bloque de elementos
  async getElementosPagina(empresaId: number, pagina: number, numReg: number): Promise<void> {
    try {
      let result: ElementoModel[] = [];
      if (!MainComponent.getInstance().cancelar) {
        const res = await this.http.get<ElementoModel[]>(MainComponent.getInstance().urlApiEcoSAT + '/api/elementos/lite/' +
          empresaId + '/' + pagina + '/' + this.ELEM_PAG).toPromise();
        if (res && res.length > 0) {
          const resultLength = res.length;
          const totalReg = res[0].TotalRegistros;
          res.forEach((elem, i) => {
            if (!elem.FechaBaja) {
              if (elem.FechaUltimaLecturaVolumetrico) {
                elem.FechaUltimaLecturaVolumetrico = new Date(elem.FechaUltimaLecturaVolumetrico);
              }
              if (!DateUtils.isValidDate(elem.FechaUltimaLecturaVolumetrico)) {
                elem.FechaUltimaLecturaVolumetrico = new Date(1970, 1, 1);
              }
              result.push(elem);
            }
          });
          if (result.length > 0) {
            MainComponent.getInstance().onNewElements(totalReg, result);
          }
          if (numReg + resultLength < totalReg) {
            await this.getElementosPagina(empresaId, pagina + 1, numReg + resultLength);
          }
        }
      } else {
        MainComponent.getInstance().logError('Proceso cancelado por el usuario');
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
  }

  // Recupera las empresas del SSO
  async getEmpresasSSO() {
    let result: EmpresaSSOModel[];
    try {
      result = await this.http.get<EmpresaSSOModel[]>(AppComponent.getInstance().urlApiSSO + '/sso/empresas').toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Recupera las empresas de la intranet
  async getEmpresasIntranet() {
    let result: EmpresaIntraModel[];
    try {
      result = await this.http.get<EmpresaIntraModel[]>(MainComponent.getInstance().urlApiIntranet + '/empresas/activas').toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Recupera las aplicaciones de la empresa
  async getAplicacionesEmpresaSSO(empresaId: number) {
    let result: AplicacionModel[];
    try {
      result = await this.http.get<AplicacionModel[]>(AppComponent.getInstance().urlApiSSO +
        '/sso/aplicaciones/empresa/' + empresaId).toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Recupero las URLs de API de las aplicaciones de la empresa
  async getAplicacionesConfigEmpresaSSO(empresaId: number) {
    let result: AplicacionEmpresaModel[];
    try {
      result = await this.http.get<AplicacionEmpresaModel[]>(AppComponent.getInstance().urlApiSSO +
        '/sso/empresa/aplicaciones/' + empresaId).toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Borro todos los elementos de una empresa
  async deleteElementosEmpresa(empresaId: number) {
    let result = false;
    try {
      result = await this.http.delete<boolean>(MainComponent.getInstance().urlApi +
        '/api/elementos?empresaId=' + empresaId).toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Recupera los equipamientos de la BDT
  async getEquipamientosBDT(): Promise<any[]> {
    let result: BdtEquipamientoModel[] = [];
    try {
      if (!MainComponent.getInstance().cancelar) {
        const res: any = await this.http.get(MainComponent.getInstance().urlApiBDT +
          '/equipamientos?include=clases&include=clases;marca;modelo;residuo;elemento').toPromise();
        if (res && res.rows) {
          result = res.rows;
          res.rows.forEach(equip => {
            equip.unidades = 0;
            equip.icono = null;
            if (equip.clases && equip.clases.length > 0) {
              equip.clases.forEach((clase, i) => {
                equip.claseStr = (i === 0 ? clase.nombre : (equip.claseStr + ', ' + clase.nombre));
              });
            }
            if (equip.residuos && equip.residuos.length > 0) {
              equip.residuos.forEach((residuo, i) => {
                equip.residuoStr = (i === 0 ? residuo.nombre : (equip.residuoStr + ', ' + residuo.nombre));
              });
            } else {
              if (equip.residuo) {
                equip.residuoStr = equip.residuo.nombre;
              }
            }
            // if (equip.capacidad_kilos < 1) {
            //   equip.capacidad_kilos = equip.capacidad_litros;
            // }
            ApiService.equipamientos.set(equip.Id, equip);
          });
        }
      } else {
        MainComponent.getInstance().logError('Proceso cancelado por el usuario');
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Recupera los equipamientos de la BDT de una empresa
  async getEquipamientosBDTEmpresa(empresaId: number): Promise<any[]> {
    let result: BdtEquipamientoModel[] = [];
    try {
      if (!MainComponent.getInstance().cancelar) {
        const res: any = await this.http.get(MainComponent.getInstance().urlApiBDT +
          '/catalogoEquipamiento/' + empresaId + '/empresa').toPromise();
        if (res && res.rows) {
          res.rows.forEach(row => {
            if (row.equipamiento) {
              result.push(row.equipamiento);
            }
          });
          result.forEach(equip => {
            equip.unidades = 0;
            equip.icono = null;
            equip.claseStr = '';
            if (equip.clases && equip.clases.length > 0) {
              equip.clases.forEach((clase, i) => {
                equip.claseStr = (i < 1 ? clase.nombre : (equip.claseStr + ', ' + clase.nombre));
              });
            }
            const residuos: any = equip.residuo;
            equip.residuoStr = '';
            if (residuos && residuos.length > 0) {
              residuos.forEach((reg, i) => {
                equip.residuoStr = (i < 1 ? reg.residuo.nombre : (equip.residuoStr + ', ' + reg.residuo.nombre));
              });
            }
            // if (equip.capacidad_kilos < 1) {
            //    equip.capacidad_kilos = equip.capacidad_litros;
            // }
            ApiService.equipamientos.set(equip.id, equip);
          });
        }
      } else {
        MainComponent.getInstance().logError('Proceso cancelado por el usuario');
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Crea un registro de catálogo de equipamiento
  async saveCatEquipamiento(catEquip: BdtCatalogoEquipModel): Promise<boolean> {
    let response = false;
    if (!MainComponent.getInstance().cancelar) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      try {
        response = await this.http.post<boolean>(MainComponent.getInstance().urlApiBDT + '/catalogoEquipamiento',
          JSON.stringify(catEquip), httpOptions).toPromise();
      } catch (e) {
        MainComponent.getInstance().logError(e.message);
      }
    } else {
      MainComponent.getInstance().logError('Proceso cancelado por el usuario');
    }
    return response;
  }

  // Crea un elemento
  async saveElemento(elemento: ElementoNPModel): Promise<ElementoNPModel> {
    let response: ElementoNPModel = null;
    if (!MainComponent.getInstance().cancelar) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      try {
        response = await this.http.post<ElementoNPModel>(MainComponent.getInstance().urlApi + '/api/elemento',
          JSON.stringify(elemento), httpOptions).toPromise();
      } catch (e) {
        MainComponent.getInstance().logError(e.message);
      }
    } else {
      MainComponent.getInstance().logError('Proceso cancelado por el usuario');
    }
    return response;
  }

  // Crea un conjunto de elementos
  async saveElementos(elementos: ElementoNPModel[]): Promise<boolean> {
    let response = false;
    if (!MainComponent.getInstance().cancelar) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      try {
        elementos.forEach(elem => {
          if (!elem.UltFechaVolum) {
            elem.UltFechaVolum = new Date(1900, 1, 1);
          }
        });
        response = await this.http.post<boolean>(MainComponent.getInstance().urlApi + '/api/save/elementos',
          JSON.stringify(elementos), httpOptions).toPromise();
      } catch (e) {
        MainComponent.getInstance().logError(e.message);
      }
    } else {
      MainComponent.getInstance().logError('Proceso cancelado por el usuario');
    }
    return response;
  }

  // Llama a la api para que recupere los móviles de la BDT
  async saveMoviles(empresaId: number): Promise<MovilModel[]> {
    let result: MovilModel[] = null;
    try {
      // Mando recuperar la flota de la BDT
      MainComponent.getInstance().logInfo('Recuperando flota de la BDT');
      const res: any = await this.http.get(MainComponent.getInstance().urlApi +
        '/api/bdt/flota?empresa=' + empresaId).toPromise();
      // Recupero los móviles de la NP
      if (res && res.state === 'ok') {
        MainComponent.getInstance().logSuccess('Ok');
        MainComponent.getInstance().logInfo('Recuperando flota de la empresa');
        result = await this.http.get<MovilModel[]>(MainComponent.getInstance().urlApi +
          '/api/moviles?empresaId=' + empresaId).toPromise();
      }
      if (!result || result.length < 1) {
        MainComponent.getInstance().logError('No se han recibido móviles de la empresa');
      } else {
        MainComponent.getInstance().logSuccess('Ok');
      }
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Recupera la última posición de todos los móviles
  async getUltimasPosiciones(empresa: number): Promise<PosicionModel[]> {
    let result: PosicionModel[] = null;
    try {
      result = await this.http.get<PosicionModel[]>(MainComponent.getInstance().urlApiEcoSAT +
        '/api/posiciones/ultimas/' + empresa).toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  // Crea una lista de posiciones
  async savePosiciones(posiciones: PosicionNPModel[]): Promise<boolean> {
    let result = false;
    try {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      result = await this.http.post<boolean>(MainComponent.getInstance().urlApi + '/api/posiciones/save',
        JSON.stringify(posiciones), httpOptions).toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return result;
  }

  async saveEmpresa(data: any): Promise<EmpresaSSOModel> {
    let response: EmpresaSSOModel = null;
    if (!MainComponent.getInstance().cancelar) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      try {
        response = await this.http.post<EmpresaSSOModel>(MainComponent.getInstance().urlApiSSO +
          '/sso/empresa/crear', JSON.stringify(data), httpOptions).toPromise();
      } catch (e) {
        MainComponent.getInstance().logError(e.message);
      }
    } else {
      MainComponent.getInstance().logError('Proceso cancelado por el usuario');
    }
    return response;
  }

  async getEmpresaApp(empresa: number, app: number): Promise<AplicacionEmpresaModel> {
    try {
      return await this.http.get<AplicacionEmpresaModel>(MainComponent.getInstance().urlApiSSO +
        '/sso/aplicacion/empresa?appId=' + app + '&enterprise=' + empresa).toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return null;
  }

  async getEmpresaGestion(idGestion: number): Promise<EmpresaSSOModel> {
    try {
      return await this.http.get<EmpresaSSOModel>(MainComponent.getInstance().urlApiSSO +
        '/sso/empresa/intranet/' + idGestion).toPromise();
    } catch (e) {
      MainComponent.getInstance().logError(e.message);
    }
    return null;
  }

  async saveEmpresaApp(data: any): Promise<AplicacionEmpresaModel> {
    let response: AplicacionEmpresaModel = null;
    if (!MainComponent.getInstance().cancelar) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      try {
        response = await this.http.post<AplicacionEmpresaModel>(MainComponent.getInstance().urlApiSSO +
          '/sso/aplicacion/empresa/crear', JSON.stringify(data), httpOptions).toPromise();
      } catch (e) {
        MainComponent.getInstance().logError(e.message);
      }
    } else {
      MainComponent.getInstance().logError('Proceso cancelado por el usuario');
    }
    return response;
  }



  async getUsuarioAcceso(usuario: string, empresa: number, app: number, rol: number): Promise<AplicacionEmpresaModel> {
    if (!MainComponent.getInstance().cancelar) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      const data = {
        UsuarioLogin: usuario,
        EmpresaId: empresa,
        AplicacionId: app,
        RolId: rol
      }
      try {
        return await this.http.post<AplicacionEmpresaModel>(MainComponent.getInstance().urlApiSSO +
          '/sso/usuario/acceso', JSON.stringify(data), httpOptions).toPromise();
      } catch (e) {
        MainComponent.getInstance().logError(e.message);
      }
    } else {
      MainComponent.getInstance().logError('Proceso cancelado por el usuario');
    }
    return null;
  }

  async saveAccesoUsuarioEmpApp(data: any): Promise<AplicacionEmpresaModel> {
    let response: AplicacionEmpresaModel = null;
    if (!MainComponent.getInstance().cancelar) {
      const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      };
      try {
        response = await this.http.post<AplicacionEmpresaModel>(MainComponent.getInstance().urlApiSSO +
          '/sso/usuario/crearacceso', JSON.stringify(data), httpOptions).toPromise();
      } catch (e) {
        MainComponent.getInstance().logError(e.message);
      }
    } else {
      MainComponent.getInstance().logError('Proceso cancelado por el usuario');
    }
    return response;
  }

  public async getEmpresasApp(appId: number): Promise<EmpresaSSOModel[]> {
    try {
      return await this.http.get<EmpresaSSOModel[]>(AppComponent.getInstance().urlApiSSO +
        '/sso/empresas/aplicacion/' + appId).toPromise();
    } catch (e) {
      console.log(e);
    }
    return null;
  }

}
